/**
 * Page actions: things a user does ON a page, granted separately from opening
 * it on the Roles & Permissions page. The API's catalog is the source of truth
 * (shipengine_api/src/modules/company-roles/menu-catalog.ts, SYSTEM_ACTIONS);
 * `npm run check:menus` fails when this list and that one disagree.
 *
 * Ids name the thing acted on, not the page: `label.void` is the same
 * permission on Orders, Tracking History and Facility Shipments.
 *
 * Components ask `hasAction('label.void')` and HIDE what it refuses. The API
 * enforces the same ids with `@RequireAction`, so hiding is convenience, not
 * the gate.
 */
export const ACTION_KEYS = [
  'orders.view',
  // The order page's shipment section, apart from the Tracking History menu.
  'orders.shipments',
  'label.create',
  'orders.ship_together',
  'label.download',
  'label.void',
  'orders.manual_tracking',
  'orders.edit_address',
  'orders.sync',
  'comments.add',
  'orders.delete',
  // Admin tier: set an order's status by hand (Orders list, inline).
  'orders.edit_status',
  'returns.create',
  'returns.process',
  'returns.settings',
  'shipment.edit',
  'shipment.pickup',
  'shipment.pick_pack',
  'shipment.hold',
  'shipment.print_override',
  'billing_report.export',
  'billing_report.edit',
  'billing_report.review_flags',
  'analytics.refresh',
  'transit.import',
  'products.edit',
  'products.import',
  'tags.manage',
  'repricing.manage',
  'sku_margins.fees',
  'competitor_prices.manage',
  'boxes.manage',
  'wms.setup',
  'wms.supervise',
  'wms.count',
  'wms.close',
  'wms.receive',
  'wms.move',
  'wms.transfer',
  'reviews.write',
  // Admin tier: every vertical's review queue, not just the caller's.
  'reviews.all_queues',
  'reviews.manage',
  'suggestions.write',
  'po.create',
  'po.edit',
  'po.receive',
  'po.cost_range',
  'po.finalize',
  'po.unfinalize',
  'po.costing',
  'po.delete',
  'suppliers.manage',
  'vendors.manage',
  'inventory.edit',
  'prices.publish',
  'facilities.manage',
  'tickets.write',
  'tickets.contact_customer',
  'logs.delete',
  // Admin areas: only company admins and the SubAdmin role can hold these.
  'logs.clear',
  'diagnostics.run',
  'carriers.manage',
  'integrations.manage',
  'templates.edit',
  'company_settings.edit',
  'review_questions.edit',
  'api_keys.manage',
  'team.manage',
  'roles.manage',
  'billing.manage',
] as const;

export type ActionKey = (typeof ACTION_KEYS)[number];

/**
 * Present in a role's list once it has been saved with actions. A list
 * without it comes from a session that predates actions: the API reads it
 * with the role's old actions, so the app shows everything rather than
 * guessing, and the next profile refresh brings the real list.
 */
export const ACTIONS_MARKER = 'actions:v2';
